import type { Item } from '@/domain/types';

/**
 * The model answer for a written question, in whichever shape its type carries:
 * a sample answer for "short", one per labelled part for "free", or a worked
 * solution and a final answer for "work".
 *
 * Everything here is imported text and is rendered as text — React escapes it,
 * and nothing on this path touches innerHTML.
 */

export function Rubric({ points, heading = 'A full answer includes' }: { points?: string[]; heading?: string }) {
  if (!points?.length) return null;
  return (
    <div className="stack" style={{ gap: 4 }}>
      <span className="flashcard__label">{heading}</span>
      <ul className="rubric">
        {points.map((point, i) => (
          <li key={i}>{point}</li>
        ))}
      </ul>
    </div>
  );
}

/** The prompts he is answering. "free" questions ask several things at once. */
export function QuestionPrompts({ item }: { item: Item }) {
  if (item.type !== 'free' || !item.parts?.length) return null;
  return (
    <ol className="parts">
      {item.parts.map((part) => (
        <li key={part.label} className="parts__item">
          <span className="parts__label">{part.label}</span>
          <span>{part.prompt}</span>
        </li>
      ))}
    </ol>
  );
}

export function ModelAnswer({ item }: { item: Item }) {
  if (item.type === 'free') {
    return (
      <div className="stack">
        {(item.parts ?? []).map((part) => (
          <div key={part.label} className="stack" style={{ gap: 4 }}>
            <span className="flashcard__label">Part {part.label}</span>
            <div className="muted" style={{ whiteSpace: 'pre-wrap' }}>
              {part.prompt}
            </div>
            <div className="sample">{part.sample}</div>
            <Rubric points={part.rubric} />
          </div>
        ))}
        <Rubric points={item.rubric} heading="Overall, a full answer includes" />
      </div>
    );
  }

  if (item.type === 'work') {
    return (
      <div className="stack">
        <span className="flashcard__label">Worked solution</span>
        <ol className="steps">
          {(item.steps ?? []).map((step, i) => (
            <li key={i}>{step}</li>
          ))}
        </ol>
        <div className="spread">
          <span className="flashcard__label">Answer</span>
          <span className="mono" style={{ fontSize: '1.1rem' }}>
            {item.answer}
          </span>
        </div>
        <Rubric points={item.rubric} />
      </div>
    );
  }

  return (
    <div className="stack">
      <span className="flashcard__label">Sample answer</span>
      <div className="sample">{item.sample}</div>
      <Rubric points={item.rubric} />
    </div>
  );
}
